import { useEffect, useMemo, useState } from 'react'
import { Calendar, Views, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { AgendasTypes, IAgenda, IAppointment, IPatient, SnackbarType } from '../../../../constants/types';
import { patientFullName } from '../../../../utils';
import Modal from '../../../../components/general/modal';
import { Alert, Button, Grid, Snackbar, Typography } from '@mui/material';

import { IconGenerator } from '../../../../components/general/mini_components';
import moment from 'moment';
import { turnsAgendaDates, isTimeSlotAvailableInAnyAgenda } from '../../../../utils/agenda';
import { Translate } from 'react-localize-redux';
import { CustomEvent, eventStyleGetter } from './calendarStyles';
import { IRequest, RequestStatus, RequestType } from '../../Request/types';
import { HOSPITAL_PATIENT } from '../../../../routes/urls';
import NewAppointment from './NewAppointment';
import { FormMakeAppointment } from '../FormAppointment';
import { fetchPatient } from '../../../../db';
import { useSelector } from 'react-redux';


interface Event {
    id: number;
    title: string;
    start: Date;
    end: Date;
    resourceId: number | number[]; // Adjust based on your resourceId type
}

export interface MultiAgendaProps {
    patients: IPatient[] // Replace 'any' with the actual type if known
    appointments: IAppointment[] // Replace 'any' with the actual type if known
    agendas: IAgenda[] // Replace 'any' with the actual type if known
    date: Date,
    typeAgenda:AgendasTypes,
    lastUpdate: number,
    showSnackbar:SnackbarType,
    extraForm? : number,
    canCreateAppointments: boolean,
    uuidInvestigation: string,
    request?: IRequest,
    preSelectedPatientUuid?: string,
    cancelCallback: (uuidAgenda:string, byUser:boolean) => void,
    showUpCallback: (uuidAgenda:string) => void,
    callbackSetSnackbar: (showSnackbar:SnackbarType) => void;
    appointmentCreatedCallback: (appointment:IAppointment) => void;
    appointmentErrorCallback: (error:any) => void;
}

export default function MultiAgenda({ date, appointments, agendas, request, patients, showSnackbar, uuidInvestigation, lastUpdate, typeAgenda, extraForm, canCreateAppointments,
                                        preSelectedPatientUuid, callbackSetSnackbar, appointmentCreatedCallback, appointmentErrorCallback, cancelCallback, showUpCallback }: MultiAgendaProps) {
    const [showModal, setShowModal] = useState(false);
    const [appointment, setAppointment] = useState<IAppointment | null>(null);
    const [newAppointment, setNewAppointment] = useState<IAppointment | null>(null);
    const [showNewAppointment, setShowNewAppointment] = useState(false);
    const [fetchedPatient, setFetchedPatient] = useState<IPatient | null>(null);
    const researchers = useSelector((state: any) => 
        state.hospital.data.researchers ? state.hospital.data.researchers : []
      );
    const translateString = useMemo(() => {
        return (`pages.hospital.outpatients.calendar.modal.${typeAgenda === AgendasTypes.CONSULTATION ? "outpatients" : "surgery"}`);
    }, [typeAgenda])

    const events = useMemo(() => {
        
        return appointments.map((appointment, index) => {
            const uuidPatient = appointment.patient.uuid;
            const patient = patients.find(pat => pat.uuid === uuidPatient);
            const patientName = patient ? patientFullName(patient?.personalData) : "Unknown";
            const startDateTime = new Date(appointment.startDateTime);
            const twoHoursAgo = new Date(new Date().getTime() - 2 * 60 * 60 * 1000);
            const appointmentStatus = (appointment.requestAppointment.status === RequestStatus.PENDING_APPROVAL) && startDateTime < twoHoursAgo ? RequestStatus.EXPIRED : appointment.requestAppointment.status;
            const surgeryIndication = appointment.requestAppointment.typeSpecificData?.ict;
            console.log("Surgeon: ", appointment?.mainSurgeon);
            const surgeon = appointment.mainSurgeon ? researchers.find(researcher => researcher.uuid === appointment.mainSurgeon.researcher.uuid) : null;
            const surgicalTeam = appointment.surgicalTeam ? appointment.surgicalTeam.map(team => researchers.find(researcher => researcher.uuid === team.researcher.uuid)) : [];
            console.log(appointment.requestAppointment.service);
            const serviceDescription = appointment.requestAppointment ? appointment.requestAppointment.service.description : null;
            console.log("Surgery Indication: ", surgeryIndication);
            console.log("StartDate: ", startDateTime);
            return {
                id: appointment.id,
                title: patientName,
                uuidPatient: uuidPatient,
                reason: appointment.reasonVisit,
                notes: appointment.notes,
                type: appointmentStatus,
                start: startDateTime,
                surgeryIndication: surgeryIndication,
                surgeon: surgeon,
                surgicalTeam: surgicalTeam,
                serviceDescription: serviceDescription,
                end: new Date(appointment.endDateTime),
                resourceId: appointment.agendaId
            }
        })
    }, [appointments, lastUpdate])
    
    const resourceMap = useMemo(() => {
        return agendas.sort((a, b) => a.name.localeCompare(b.name)).map((agenda, index) => {
            return {
                resourceId: agenda.id,
                resourceTitle: agenda.name
            }
        })
    }, [agendas, lastUpdate])
    
    const [minHour, maxHour] = useMemo(() => {
        let minHour = 24;
        let maxHour = 0;
        agendas.forEach(agenda => {
            const [startTurn, endTurn] = turnsAgendaDates(agenda.turn);
            const startHour = startTurn.getHours();
            const endHour = endTurn.getHours();
            if(startHour < minHour){
                minHour = startHour;
            }
            if(endHour > maxHour){
                maxHour = endHour;
            }
        })
        return [minHour, maxHour];
    }, [agendas])
    
    const { defaultDate, views } = useMemo(
        () => ({
            defaultDate: date,
            views: ['day'],
        }),
        [date]
    )

    useEffect(() => {
        resetModal();
    }, [appointments.length])

    useEffect(() => {
        if (showSnackbar.show && showSnackbar.severity === "success") {
            setShowModal(false);
            setAppointment(null);
            setNewAppointment(null);
            setFetchedPatient(null);
            setShowNewAppointment(false);
        }
    }, [showSnackbar.show]);

    useEffect(() => {
        async function fetchPatientData() {
            if (showNewAppointment && appointment) {
                const patient = await fetchPatient(appointment.patient.uuid);
                setFetchedPatient(patient);
            }
        }
        fetchPatientData();
    }, [showNewAppointment, appointment]);

    function handleSelectEvent(event:Event) {
        console.log("Selected event: ", event);
        setShowModal(true);
        const appointment = appointments.find(app => app.id === event.id);
        setAppointment(appointment);
    }

    function cancelAppointment(byUser:boolean){
        if(appointment){
            cancelCallback(appointment.uuid, byUser);
        }
    }

    function showUpAppointment(){
        if(appointment){
            showUpCallback(appointment.uuid);
        }
    }

    function sheduleNewAppointment(){
        setShowNewAppointment(true);
    }

    function renderAllOptionsModalCore(typeAgenda:AgendasTypes){
        
        return(<>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Button onClick={() => cancelAppointment(false)}>
                        <IconGenerator type="cancel_appointment" size="large" />
                    </Button>
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.cancel_appoinment`} />
                    </Typography>
                </Grid>
                {
                    appointment?.requestAppointment.status !== RequestStatus.CHECKED_IN &&
                    <Grid item xs={12}>
                        <Button onClick={showUpAppointment}>
                            <IconGenerator type="show_up" size="large" />
                        </Button>  
                        <Typography variant="body1" component="span" gutterBottom>
                            <Translate id={`${translateString}.check_in_patient`} />
                        </Typography>  
                    </Grid>
                }
                {
                    typeAgenda === AgendasTypes.CONSULTATION &&
                <>
                <Grid item xs={12}>
                    <Button onClick={sheduleNewAppointment}>
                        <IconGenerator color="black" type="outpatients" size="large" />
                    </Button>     
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.another_appointment`} />
                    </Typography>             
                </Grid>
                
                <Grid item xs={12}>
                    <Button onClick={() => cancelAppointment(true)}>
                        <IconGenerator type="cancel_appointment_by_user" size="large" />
                    </Button>
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.cancel_appoinment_by_user`} />
                        </Typography>
                        </Grid>
                    </>
                }
            </Grid>
        </>)
    }

    function renderPendingPaymentModalCore(){
        return(<>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Button onClick={() => cancelAppointment(false)}>
                    <IconGenerator type="cancel_appointment" size="large" />
                </Button>
                <Typography variant="body1" component="span" gutterBottom>
                    <Translate id={`${translateString}.cancel_appoinment`} />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Button onClick={sheduleNewAppointment}>
                    <IconGenerator color="black" type="outpatients" size="large" />
                </Button>     
                <Typography variant="body1" component="span" gutterBottom>
                    <Translate id={`${translateString}.title`} />
                </Typography>             
            </Grid>
        </Grid>
        </>)
    }
    function renderModalAppointmentCore(){
        if(typeAgenda === AgendasTypes.CONSULTATION){
            switch(appointment?.requestAppointment.status){
                case RequestStatus.PENDING_APPROVAL:
                    return (
                        renderAllOptionsModalCore()
                    );
                case RequestStatus.CANCELED_BY_USER:
                case RequestStatus.PENDING_APPROVAL:
                    return (
                        renderPendingPaymentModalCore()
                    );
                case RequestStatus.PENDING_PAYMENT:
                    return (
                        <Grid container>
                            <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                <Typography variant="body1" component="p" >
                                    <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.pending_payment.message" />
                                </Typography>
                            </Grid>
                            <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                <Typography component="p" variant="body1" >
                                <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.pending_payment.message_2" />
                                <Button onClick={() => {
                                    const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", appointment!.patient.uuid)
                                    console.log("Next url", nextUrl);
                                    //history.push(nextUrl);
                                    window.open(nextUrl, '_blank');
                                }}>
                                    <IconGenerator type="view" size="large" />
                                </Button>
                                </Typography>
                            </Grid>
                        </Grid>
                    ); 
                default:
                    const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", appointment!.patient.uuid)
                    console.log("Next url", nextUrl);
                    //history.push(nextUrl);
                    window.open(nextUrl, '_blank');
                    resetModal();
            }
        }
        else if(typeAgenda === AgendasTypes.SURGERY){
            return (
                renderAllOptionsModalCore(typeAgenda)
            );
        }
    }

    function renderCoreModal() {
        if (showNewAppointment && appointment && fetchedPatient) {
            return (
                <FormMakeAppointment
                    uuidPatient={appointment.patient.uuid}
                    uuidInvestigation={uuidInvestigation}
                    showAllAgendas={false}
                    hidePatientInfo={false}
                    extraForm={extraForm}
                    typeAgenda={typeAgenda}
                    dateTimeAppointment={true}
                    cancelCallback={resetModal}
                    phoneNumber={fetchedPatient.personalData.phone}
                    request={request}
                    appointmentMadeCallback={(date) => appointmentCreatedCallback(date)}
                    
                />
            );
        } else if (appointment) {
            return renderModalAppointmentCore();
        } else if (newAppointment) {
            return (
                <NewAppointment
                    request={request}
                    appointmentInfo={newAppointment}
                    extraForm={extraForm}
                    typeAgenda={typeAgenda}
                    preSelectedPatientUuid={preSelectedPatientUuid}
                    cancelCallback={resetModal}
                    appointmentCreatedCallback={appointmentCreatedCallback}
                    appointmentErrorCallback={appointmentErrorCallback}
                />
            );
        }
    }

    function resetModal() {
        setShowModal(false);
        setAppointment(null);
        setNewAppointment(null);
        setFetchedPatient(null);
        setShowNewAppointment(false);
    }

    function slotPropGetter(date: Date, resourceId?: string | number) {
        const numericResourceId = typeof resourceId === 'string' ? parseInt(resourceId, 10) : resourceId;
        const isAvailable = isTimeSlotAvailableInAnyAgenda(date, agendas, numericResourceId);
        
        if (!isAvailable) {
            return {
                style: {
                    backgroundColor: '#d0d0d0',
                    opacity: 1,
                    cursor: 'not-allowed',
                },
            };
        }
        
        return {};
    }

    function handleOnClickSlot(slotInfo:any){
        if(!canCreateAppointments){
            return null;
        }
        const { start, resourceId } = slotInfo;
        console.log('Start time:', start);
        console.log('Resource ID:', resourceId);

        // Check if the time slot is available
        const isAvailable = isTimeSlotAvailableInAnyAgenda(start, agendas, resourceId);
        if (!isAvailable) {
            callbackSetSnackbar({
                show: true,
                severity: 'warning',
                message: 'pages.hospital.outpatients.appointment.error.outside_agenda_hours'
            });
            return;
        }

        const currentAgenda = agendas.find(agenda => agenda.id === resourceId);
        console.log('Current Agenda:', currentAgenda);
        // Get just the hour of the clicked date
        const clickedHour = start.getHours();
        console.log('Clicked Hour:', clickedHour);
        const newAppointment = {
            startDateTime: start,
            agendaId: resourceId,
        }
        setNewAppointment(newAppointment);
        setShowModal(true);
        
    }

    if(agendas.length === 0){
        return "No Agendas"
    }
    return (
    <>
        <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={() => callbackSetSnackbar({ show: false })}>
                <div>
                    {
                        (showSnackbar.message && showSnackbar.severity) &&
                        <Alert onClose={() => callbackSetSnackbar({ show: false })} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>
        <Modal open={showModal} closeModal={resetModal} 
            title={appointment ? <Translate id={`${translateString}.existing_appointment.title`} /> : <Translate id={`${translateString}.new_appointment.title`   } />} >
            <>
            {
                renderCoreModal()
            }
            </>
        </Modal>
        <div className="height600">
            <Calendar
                key={defaultDate.toISOString()}
                defaultDate={defaultDate}
                defaultView={Views.DAY}
                events={events}
                localizer={momentLocalizer(moment)}
                resourceIdAccessor="resourceId"
                resources={resourceMap}
                formats={{ eventTimeRangeFormat: () => null }}
                resourceTitleAccessor="resourceTitle"
                step={15}
                timeslots={1} 
                selectable={true} 
                onSelectSlot = {handleOnClickSlot}
                views={views}
                onSelectEvent={handleSelectEvent}
                components={{
                    toolbar: () => null,
                    event: CustomEvent
                }}
                eventPropGetter={eventStyleGetter}
                slotPropGetter={slotPropGetter}
                style={{ height: 1000 }}
                min={new Date(2024, 8, 21, minHour, 0, 0)} // 8:00 AM
                max={new Date(2024, 8, 21, maxHour, 0, 0)} // 6:00 PM
            />
      </div>
    </>
  )
}
